/**
 * AdminListeningCatalogue — Admin view of the 54 catalogue entries.
 * Route: /app/admin/listening-catalogue
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { RefreshCw, Loader2, ExternalLink, Sparkles } from "lucide-react";

export default function AdminListeningCatalogue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [generating, setGenerating] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/listening-catalogue");
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) { toast.error("Failed to load catalogue"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const refresh = async () => {
    if (!window.confirm("Re-scrape ieltsfever.org? This takes ~90 seconds.")) return;
    setRefreshing(true);
    try {
      const r = await api.post("/admin/listening-catalogue/refresh");
      toast.success(`Refreshed — ${r.data.upserted} tests`);
      await load();
    } catch (e) { toast.error("Refresh failed"); }
    finally { setRefreshing(false); }
  };

  const genOriginal = async (n) => {
    setGenerating(n);
    try {
      const r = await api.post("/admin/listening-catalogue/generate-original", { source_test_number: n });
      toast.message(`Blueprint #${n} created — Phase-2 generator coming next sprint.`);
    } catch (e) { toast.error("Generate failed"); }
    finally { setGenerating(null); }
  };

  return (
    <div className="min-h-screen px-6 py-8 max-w-7xl mx-auto" data-testid="admin-listening-catalogue">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase opacity-60">// ADMIN · LISTENING CATALOGUE</div>
          <h1 className="text-3xl font-bold mt-1">Listening Catalogue · {items.length} tests</h1>
        </div>
        <button onClick={refresh} disabled={refreshing} data-testid="catalogue-refresh"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-sm border disabled:opacity-50"
                style={{ borderColor: "rgba(0,0,0,0.15)" }}>
          {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Re-scrape source
        </button>
      </div>

      {loading && (
        <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      )}

      {!loading && (
        <div className="rounded-md border overflow-hidden" style={{ borderColor: "rgba(0,0,0,0.1)" }}>
          <table className="w-full text-sm">
            <thead style={{ background: "rgba(0,0,0,0.04)" }}>
              <tr className="text-left uppercase tracking-[0.2em] text-xs">
                <th className="px-4 py-3 w-16">#</th>
                <th className="px-4 py-3">Sections</th>
                <th className="px-4 py-3 w-24">Keys</th>
                <th className="px-4 py-3 w-32">Status</th>
                <th className="px-4 py-3 w-44">Source</th>
                <th className="px-4 py-3 w-40">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.test_number} className="border-t" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
                  <td className="px-4 py-3 font-mono">{t.test_number}</td>
                  <td className="px-4 py-3">
                    {(t.sections || []).map((s) => (
                      <div key={s.section_no} className="text-xs">
                        <span className="font-mono opacity-50">S{s.section_no}:</span> {s.topic}
                      </div>
                    ))}
                  </td>
                  <td className="px-4 py-3 font-mono">{t.answer_count}/40</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-mono tracking-[0.2em]"
                          style={{ background: t.status === "published" ? "rgba(22,163,74,0.12)" : "rgba(247,127,0,0.12)",
                                   color: t.status === "published" ? "#16A34A" : "#B45309" }}>
                      {t.status?.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <a href={t.external_mp3_url} target="_blank" rel="noreferrer" className="block underline opacity-70 hover:opacity-100">
                      MP3 <ExternalLink className="w-3 h-3 inline opacity-60" />
                    </a>
                    <a href={t.external_pdf_url} target="_blank" rel="noreferrer" className="block underline opacity-70 hover:opacity-100">
                      PDF <ExternalLink className="w-3 h-3 inline opacity-60" />
                    </a>
                  </td>
                  <td className="px-4 py-3 flex flex-col gap-1.5">
                    <Link to={`/app/listening-catalogue/${t.test_number}`}
                          className="px-2 py-1 rounded-sm text-xs font-mono uppercase tracking-[0.2em] border text-center"
                          style={{ borderColor: "rgba(0,0,0,0.15)" }}
                          data-testid={`admin-preview-${t.test_number}`}>
                      Preview
                    </Link>
                    <button onClick={() => genOriginal(t.test_number)} disabled={generating === t.test_number}
                            data-testid={`admin-generate-${t.test_number}`}
                            className="inline-flex items-center justify-center gap-1 px-2 py-1 rounded-sm text-xs font-mono uppercase tracking-[0.2em]"
                            style={{ background: "rgba(247,127,0,0.12)", color: "#B45309" }}>
                      {generating === t.test_number ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                      AI Original
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
